import React, { useState } from 'react';
import { get, isNil } from 'lodash';
import { injectIntl, IntlShape } from 'react-intl';
import { arrayTranslate, translate } from 'containers/services/translate';
import messages from 'containers/common/LegalProceedings/JudgementAdd/Recap/messages';
import * as featureService from 'features/fflip-helpers';
import { FEATURES } from 'features/features';
import { getVictimFromSagisInfo } from 'containers/PreOpeningPage/utils';
import {
  EMPTY_DOCUMENT_CERTIFICATE,
  INCOME_CERTIFICATE,
  VICTIM,
} from 'containers/data-structure';
import { IUploadedDocument } from 'containers/LawyerLegalProceedingsPage/typings';
import { StyledIncomeCertificateDrawer } from 'containers/LawyerLegalProceedingsPage/judgementAdd/JudgementAddPronouncing/styled-components';
import SahamDrawer from 'components/SahamDrawer';
import {
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  TextField,
} from '@material-ui/core';
import {
  incomeCertificatesValidator,
  incomeCertificateValidator,
} from 'containers/LawyerLegalProceedingsPage/judgementAdd/IncomeCertificate/form-validations';
import { displayErrorText } from 'containers/services/validateForm';
import { DOCUMENT_TYPES } from 'constants/global';
import UploadDocumentBloc from 'containers/common/LegalProceedings/JudgementAdd/Forms/JudgementAddPrononcings/UploadDocumentBloc';
import { VictimType } from 'typings/globalTypes';

export interface IncomeCertificateDrawerProps {
  intl: IntlShape;
  victim: VictimType;
  uploadDocument: Function;
  uploadedDocument: IUploadedDocument;
  resetUploadedDocument: Function;
  sagisVictimInfo: any;
  isManager: Boolean;
  setDocumentId?: Function;
  documentId: any;
  claimId: number;
  claimNumber: string;
  open: boolean;
  handleToggleDrawer: any;
  incomeCertificates: any;
  setIncomeCertificates: Function;
  handleOpenConfirmationDialog: Function;
  setSelectedIncomeCertificate: Function;
}

export const IncomeCertificateDrawer: React.FC<IncomeCertificateDrawerProps> = (
  props,
): JSX.Element => {
  const [incomeCertificates, setIncomeCertificates] = useState({
    ...props.incomeCertificates,
  });

  const [documentType, setDocumentType] = React.useState(
    get(get(props.incomeCertificates, [props.victim.id as any], {}), 'type'),
  );
  const inputLabel = React.useRef<HTMLLabelElement>(null);
  const handleChange = (victim: VictimType) => (
    event: React.ChangeEvent<{
      value: string;
      name: string;
    }>,
  ) => {
    const {
      target: { value, name },
    } = event;

    setIncomeCertificates({
      ...incomeCertificates,
      [victim.id as any]: {
        ...incomeCertificates[victim.id as any],
        [name]: name === INCOME_CERTIFICATE.INCOME ? Number(value) : value,
      },
    });
  };

  const setDocument = (victimId: number) => (document: any) => {
    const incomeCertificatesData = Object.assign({}, { ...incomeCertificates });

    victimId === props.documentId &&
      setIncomeCertificates({
        ...incomeCertificatesData,
        [victimId]: {
          ...incomeCertificatesData[victimId],
          ...document,
        },
      });
    if (props.resetUploadedDocument) props.resetUploadedDocument();
  };
  const incomeCertificateValidation = incomeCertificateValidator(
    incomeCertificates[props.victim?.id as any],
  );

  const translateArrayErrorMessages = arrayTranslate(props.intl.formatMessage);

  const { message } =
    displayErrorText(
      incomeCertificateValidation,
      translateArrayErrorMessages(incomeCertificateValidation, true),
    ) || {};

  const handleValidateDrawer = () => {
    props.setIncomeCertificates(incomeCertificates);
    props.setSelectedIncomeCertificate({
      ...incomeCertificates[props.victim?.id as any],
    });
    props.handleOpenConfirmationDialog();
    props.handleToggleDrawer();
  };
  const validateIncomeCertificates = incomeCertificatesValidator(
    Object.values(incomeCertificates),
  );

  return (
    <SahamDrawer
      open={props.open}
      handleToggleDrawer={props.handleToggleDrawer}
      title={
        featureService.hasFeature(FEATURES.MIGRATION_CLAIM_INFO)
          ? `${get(
              getVictimFromSagisInfo(
                get(props.victim, 'victimKey'),
                props.sagisVictimInfo,
              ),
              'victimFirstName',
              '',
            )} ${get(
              getVictimFromSagisInfo(
                get(props.victim, 'victimKey'),
                props.sagisVictimInfo,
              ),
              'victimLastName',
              '',
            )}`
          : `${props.victim[VICTIM.FIRST_NAME]} ${
              props.victim[VICTIM.LAST_NAME]
            }`
      }
      confirmButtonTitle={translate(messages.save, props)}
      handleValidateDrawer={handleValidateDrawer}
      isSaveButtonDisabled={!validateIncomeCertificates && isNil(documentType)}
    >
      <StyledIncomeCertificateDrawer>
        <Grid container direction={'column'} spacing={2} key={props.victim.id}>
          {!!incomeCertificates[props.victim?.id as any] && (
            <React.Fragment>
              <Grid item xs={12}>
                <TextField
                  label={translate({ ...messages.income }, props)}
                  name={INCOME_CERTIFICATE.INCOME}
                  data-testid={INCOME_CERTIFICATE.INCOME}
                  value={get(
                    incomeCertificates[props.victim.id as any],
                    INCOME_CERTIFICATE.INCOME,
                    null,
                  )}
                  onChange={handleChange(props.victim)}
                  type="number"
                  error={get(message, INCOME_CERTIFICATE.INCOME)}
                  helperText={get(message, INCOME_CERTIFICATE.INCOME)}
                  fullWidth
                />
              </Grid>
              <Grid item xs={12}>
                <FormControl fullWidth>
                  <InputLabel
                    ref={inputLabel}
                    id="simple-select-outlined-label-procedure"
                  >
                    {translate({ ...messages.typeDocument }, props)}
                  </InputLabel>
                  <Select
                    fullWidth
                    value={documentType}
                    onChange={(e: React.ChangeEvent<{ value: unknown }>) => {
                      if (
                        !isNil(
                          get(
                            get(
                              props.incomeCertificates,
                              [(props.victim.id as unknown) as number],
                              {},
                            ),
                            'url',
                            null,
                          ),
                        )
                      ) {
                        props.setIncomeCertificates({
                          ...props.incomeCertificates,
                          [(props.victim.id as unknown) as number]: {
                            ...props.incomeCertificates[
                              (props.victim.id as unknown) as number
                            ],
                            type: e.target.value,
                          },
                        });
                      }
                      setDocumentType(e.target.value as any);
                    }}
                  >
                    <MenuItem value={DOCUMENT_TYPES.INCOME_CERTIFICATE}>
                      {DOCUMENT_TYPES.INCOME_CERTIFICATE}
                    </MenuItem>
                    <MenuItem value={DOCUMENT_TYPES.SCHOOLING_CERTIFICATE}>
                      {DOCUMENT_TYPES.SCHOOLING_CERTIFICATE}
                    </MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12}>
                <UploadDocumentBloc
                  document={get(
                    incomeCertificates,
                    [props.victim.id as any],
                    {},
                  )}
                  setDocument={setDocument(props.victim.id as any)}
                  index={0}
                  resetUploadedDocument={props.resetUploadedDocument}
                  uploadDocument={props.uploadDocument}
                  uploadedDocument={props.uploadedDocument}
                  docType={documentType as any}
                  setDocumentId={props.setDocumentId}
                  docId={(props.victim.id as unknown) as string}
                  deleteDoc={() =>
                    setIncomeCertificates({
                      ...incomeCertificates,
                      [props.victim.id as any]: {
                        ...incomeCertificates[props.victim.id as any],
                        ...EMPTY_DOCUMENT_CERTIFICATE,
                      },
                    })
                  }
                />
              </Grid>
            </React.Fragment>
          )}
        </Grid>
      </StyledIncomeCertificateDrawer>
    </SahamDrawer>
  );
};

export default injectIntl(IncomeCertificateDrawer);
